import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PersonOutlined,
  LockOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/auth.service';
import PasswordStrengthBar from '../../components/common/PasswordStrengthBar';

const StudentSettings: React.FC = () => {
  const { user, logout } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isSsoUser = user?.authMethod === 'oidc';

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      await authService.changePassword({ currentPassword, newPassword });
      showNotification('Password updated successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to update password';
      setPasswordError(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await authService.deleteAccount(deleteConfirmation);
      logout();
      navigate('/login');
      showNotification('Account deleted', 'info');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to delete account';
      showNotification(msg, 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmation('');
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        My Profile
      </Typography>

      {/* Account Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonOutlined color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Account Information
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1.5, alignItems: 'baseline' }}>
          <Typography variant="body2" color="text.secondary">Name:</Typography>
          <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>

          <Typography variant="body2" color="text.secondary">Email:</Typography>
          <Typography variant="body1">{user?.email}</Typography>

          <Typography variant="body2" color="text.secondary">Role:</Typography>
          <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{user?.role}</Typography>

          <Typography variant="body2" color="text.secondary">Member since:</Typography>
          <Typography variant="body1">{formatDate(user?.createdAt)}</Typography>

          <Typography variant="body2" color="text.secondary">Last login:</Typography>
          <Typography variant="body1">{formatDate(user?.lastLoginAt)}</Typography>
        </Box>
      </Paper>

      {/* Change Password - hidden for SSO users */}
      {!isSsoUser && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LockOutlined color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Change Password
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleChangePassword} sx={{ maxWidth: 400 }}>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>
            )}

            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              size="small"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              size="small"
              sx={{ mb: 0.5 }}
            />
            <PasswordStrengthBar password={newPassword} />

            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              size="small"
              error={!!confirmPassword && confirmPassword !== newPassword}
              helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
              sx={{ mb: 2, mt: 1 }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
              startIcon={passwordLoading ? <CircularProgress size={16} /> : undefined}
            >
              Update Password
            </Button>
          </Box>
        </Paper>
      )}

      {/* Danger Zone */}
      <Paper sx={{ p: 3, border: '1px solid', borderColor: 'error.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmberOutlined color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
            Danger Zone
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Delete your account and all associated data. This action cannot be undone.
        </Typography>

        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete My Account
        </Button>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeleteConfirmation(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete your account? This will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li><Typography variant="body2">Remove your personal data</Typography></li>
            <li><Typography variant="body2">Remove access to all sessions</Typography></li>
            <li><Typography variant="body2">This cannot be undone</Typography></li>
          </Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Type DELETE to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder="DELETE"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmation(''); }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteConfirmation !== 'DELETE' || deleteLoading}
            onClick={handleDeleteAccount}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : undefined}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentSettings;
